import React, { useState, useEffect, useCallback } from 'react';
import api, { extractApiError } from '../../services/api';
import toast from 'react-hot-toast';

export default function TokensPage() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTokenValue, setNewTokenValue] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/freight/tokens');
      setTokens(data.data?.tokens || []);
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao carregar tokens'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post('/freight/tokens', { name: newName.trim() });
      setNewTokenValue(data.data.token);
      setNewName('');
      load();
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao criar token'));
    } finally {
      setCreating(false);
    }
  }

  async function handleToggle(token) {
    try {
      await api.patch(`/freight/tokens/${token.id}/toggle`);
      toast.success(token.is_active ? 'Token desativado' : 'Token reativado');
      load();
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao atualizar token'));
    }
  }

  async function handleDelete(token) {
    if (!window.confirm(`Remover o token "${token.name}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/freight/tokens/${token.id}`);
      toast.success('Token removido');
      load();
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao remover token'));
    }
  }

  function handleCopy() {
    if (!newTokenValue) return;
    navigator.clipboard.writeText(newTokenValue).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function fmtDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  }

  return (
    <div style={s.root}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Tokens de API</h1>
          <p style={s.subtitle}>Gere chaves para integrar sua loja ao BringHub via header <code style={s.code}>X-Api-Key</code></p>
        </div>
        <button style={s.btnNew} onClick={() => setShowModal(true)}>+ Novo Token</button>
      </div>

      {/* Modal: criar token */}
      {showModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            {newTokenValue ? (
              <>
                <h2 style={s.modalTitle}>Token gerado</h2>
                <p style={s.modalWarn}>⚠️ Copie agora. Este valor não será exibido novamente.</p>
                <div style={s.tokenBox}>
                  <code style={s.tokenValue}>{newTokenValue}</code>
                  <button style={s.btnCopy} onClick={handleCopy}>{copied ? '✓ Copiado' : 'Copiar'}</button>
                </div>
                <button style={s.btnPrimary} onClick={() => { setShowModal(false); setNewTokenValue(null); }}>
                  Fechar
                </button>
              </>
            ) : (
              <>
                <h2 style={s.modalTitle}>Novo Token de API</h2>
                <form onSubmit={handleCreate}>
                  <label style={s.label}>Nome (identificação interna)</label>
                  <input
                    style={s.input}
                    placeholder="ex: Loja Principal, ERP Bling..."
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    autoFocus
                  />
                  <div style={s.modalActions}>
                    <button type="button" style={s.btnSecondary} onClick={() => { setShowModal(false); setNewName(''); }}>
                      Cancelar
                    </button>
                    <button type="submit" style={s.btnPrimary} disabled={creating || !newName.trim()}>
                      {creating ? 'Gerando...' : 'Gerar Token'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Lista de tokens */}
      <div style={s.card}>
        {loading ? (
          <div style={s.empty}>Carregando...</div>
        ) : tokens.length === 0 ? (
          <div style={s.empty}>
            <span style={s.emptyIcon}>🔑</span>
            <p>Nenhum token gerado ainda.</p>
            <button style={s.btnPrimary} onClick={() => setShowModal(true)}>Gerar primeiro token</button>
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                {['Nome', 'Prefixo', 'Status', 'Último uso', 'Criado em', 'Ações'].map((h) => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} style={s.tr}>
                  <td style={s.td}><strong>{t.name}</strong></td>
                  <td style={s.td}><code style={s.code}>{t.token_preview}…</code></td>
                  <td style={s.td}>
                    <span style={{ ...s.badge, ...(t.is_active ? s.badgeGreen : s.badgeGray) }}>
                      {t.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td style={s.td}>{fmtDate(t.last_used_at)}</td>
                  <td style={s.td}>{fmtDate(t.created_at)}</td>
                  <td style={s.td}>
                    <div style={s.actions}>
                      <button style={t.is_active ? s.btnWarn : s.btnSecondary} onClick={() => handleToggle(t)}>
                        {t.is_active ? 'Desativar' : 'Reativar'}
                      </button>
                      <button style={s.btnDanger} onClick={() => handleDelete(t)}>Remover</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={s.infoBox}>
        <strong>Como usar:</strong> Inclua o token no header de cada requisição:
        <pre style={s.pre}>{`curl -X POST https://api.exemplo.com/api/v1/correios/quote \\
  -H "X-Api-Key: bhf_seu_token_aqui" \\
  -H "Content-Type: application/json" \\
  -d '{"cepOrigem":"01310100","cepDestino":"20040020","peso":300,"comprimento":20,"largura":15,"altura":10}'`}</pre>
      </div>
    </div>
  );
}

const s = {
  root: { padding: '32px', maxWidth: '960px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '22px', fontWeight: '700', color: '#111827', margin: '0 0 4px' },
  subtitle: { fontSize: '14px', color: '#6b7280', margin: 0 },

  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', marginBottom: '24px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '14px 16px', fontSize: '14px', color: '#374151', verticalAlign: 'middle' },

  badge: { display: 'inline-block', padding: '2px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: '600' },
  badgeGreen: { background: '#d1fae5', color: '#065f46' },
  badgeGray: { background: '#f3f4f6', color: '#6b7280' },

  actions: { display: 'flex', gap: '8px' },
  empty: { padding: '48px 24px', textAlign: 'center', color: '#9ca3af' },
  emptyIcon: { fontSize: '36px', display: 'block', marginBottom: '12px' },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: '14px', padding: '32px', width: '480px', maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: '#111827', margin: '0 0 16px' },
  modalWarn: { background: '#fef9c3', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#92400e', marginBottom: '16px' },
  modalActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' },

  tokenBox: { background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', wordBreak: 'break-all' },
  tokenValue: { flex: 1, fontSize: '13px', color: '#1e3a8a', fontFamily: 'monospace' },

  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  code: { background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px', color: '#1e3a8a' },

  btnNew: { padding: '10px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '14px', cursor: 'pointer' },
  btnPrimary: { padding: '9px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '14px', cursor: 'pointer' },
  btnSecondary: { padding: '7px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  btnWarn: { padding: '7px 14px', background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  btnDanger: { padding: '7px 14px', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  btnCopy: { padding: '6px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },

  infoBox: { background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '12px', padding: '16px 20px', fontSize: '13px', color: '#0c4a6e', lineHeight: '1.6' },
  pre: { background: '#fff', border: '1px solid #e0f2fe', borderRadius: '8px', padding: '12px', fontSize: '12px', marginTop: '10px', overflowX: 'auto', color: '#0c4a6e', lineHeight: '1.7' },
};
